import React, { useState, useEffect, useMemo } from 'react';
import { insertTableBd, updateTableBd, deleteBd } from '../../config/functions';
import { showNotification } from '../utils/NotificationFooter';
import { showConfirmDialog } from '../utils/ConfirmDialog';
import '../../css/modalVariableConfigOpt.css';
import '../../css/modalTheme.css';

const ModalVariableConfigOpt = ({ 
    show, 
    handleClose, 
    rig,
    currentVariable, // Objeto completo con toda la info de la variable actual
    variables, // Lista de todas las variables disponibles
    updateVarMatrizId,
    uid,
    user,
    onUpdate,
    initialTab = 'variable', // 'variable' o 'alarm'
    theme, // Tema para adaptar el modal
    gridType = 'inferior', // 'inferior' o 'izquierda' - Define qué endpoints usar
    position = 0 // Posición en la grilla (para grilla izquierda es 0-5)
}) => {
    // 🎨 Usar tema recibido o valor por defecto
    const currentTheme = theme || { bgColor: '#000000', fontColor: '#ffffff', chartBorder: '#333333' };
    const [activeTab, setActiveTab] = useState(initialTab);
    const [searchTerm, setSearchTerm] = useState('');
    
    // 🔧 Configuración de endpoints según tipo de grilla
    const endpoints = {
        inferior: {
            create: 'createvarmatriz',
            update: 'updatevarmatriz'
        },
        izquierda: {
            create: 'createvarmatrizizq',
            update: 'updatevarmatrizizq'
        }
    };
    
    const currentEndpoints = endpoints[gridType] || endpoints.inferior;
    
    // Estados para cambio de variable
    const [selectedVariable, setSelectedVariable] = useState(null);
    
    // Estados para alarma
    const [minValue, setMinValue] = useState('0');
    const [maxValue, setMaxValue] = useState('0');

    // Filtrar variables por búsqueda (optimizado con useMemo)
    const filteredVariables = useMemo(() => {
        if (!variables) return [];
        if (!searchTerm.trim()) return variables;

        const searchLower = searchTerm.toLowerCase();
        return variables.filter(v => 
            v.vr_var_nom?.toLowerCase().includes(searchLower) ||
            v.vr_var_umd?.toLowerCase().includes(searchLower)
        );
    }, [variables, searchTerm]);

    // Reset al abrir modal - Solo cuando show cambia de false a true
    const prevShowRef = React.useRef(false);
    useEffect(() => {
        // Solo ejecutar cuando el modal se abre (show pasa de false a true)
        if (show && !prevShowRef.current) {
            console.log('🔵 ModalVariableConfigOpt - Abriendo modal:', {
                gridType,
                variablesLength: variables?.length || 0,
                currentVariable,
                updateVarMatrizId,
                position
            });
            
            setActiveTab(initialTab);
            setSearchTerm('');
            setSelectedVariable(null);
            setMinValue(currentVariable?.nb_alarm_min?.toString() || '0');
            setMaxValue(currentVariable?.nb_alarm_max?.toString() || '0');
            
            // Validar si hay variables disponibles
            if (!variables || variables.length === 0) {
                console.warn('⚠️ ModalVariableConfigOpt - No hay variables disponibles');
            }
        }
        prevShowRef.current = show;
    }, [show, initialTab, currentVariable, variables, gridType, updateVarMatrizId, position]);

    // Cerrar con ESC
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && show) {
                handleClose();
            }
        };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [show, handleClose]);

    // ===== FUNCIONES DE VARIABLE =====
    const handleVariableSelect = async (variable) => {
        if (updateVarMatrizId === 0) {
            // Agregar nueva variable
            try {
                const fecha = new Date();
                let dataSend;
                
                if (gridType === 'izquierda') {
                    // Endpoint para grilla izquierda
                    dataSend = {
                        nb_tldro_idn: parseInt(rig),
                        nb_var_idn: parseInt(variable.nb_var_idn),
                        nb_usurio_idn: parseInt(uid),
                        nb_datos_grafica_orden: parseInt(position),
                        vr_adt_usureg: user,
                        dt_adt_fchreg: fecha.toISOString()
                    };
                } else {
                    // Endpoint para grilla inferior (LiveDataOpt)
                    dataSend = {
                        nb_tldro_idn: parseInt(rig),
                        nb_var_idn: parseInt(variable.nb_var_idn),
                        nb_usurio_idn: uid,
                        nb_matr_fila: 0,
                        nb_matr_colum: 999, // Se ajustará en el backend
                        vr_adt_usureg: 'admin',
                        dt_adt_fchreg: fecha.toISOString()
                    };
                }
                
                await insertTableBd(currentEndpoints.create, dataSend);
                showNotification('Variable agregada correctamente', 'success');
                await onUpdate();
                handleClose();
            } catch (error) {
                console.error('Error adding variable:', error);
                showNotification('Error al agregar variable', 'error');
            }
        } else {
            // Actualizar variable existente
            try {
                const fecha = new Date();
                let dataSend;
                
                if (gridType === 'izquierda') {
                    // Endpoint para grilla izquierda
                    dataSend = {
                        nb_datos_grafica_idn: parseInt(updateVarMatrizId),
                        nb_tldro_idn: parseInt(rig),
                        nb_var_idn: parseInt(variable.nb_var_idn),
                        nb_usurio_idn: parseInt(uid),
                        vr_adt_usureg: user,
                        dt_adt_fchreg: fecha.toISOString()
                    };
                } else {
                    // Endpoint para grilla inferior (LiveDataOpt)
                    dataSend = {
                        nb_matr_idn: parseInt(updateVarMatrizId),
                        nb_var_idn: parseInt(variable.nb_var_idn),
                        vr_adt_usureg: user,
                        dt_adt_fchreg: fecha.toISOString()
                    };
                }
                
                await updateTableBd(currentEndpoints.update, dataSend);
                showNotification('Variable actualizada correctamente', 'success');
                await onUpdate();
                handleClose();
            } catch (error) {
                console.error('Error updating variable:', error);
                showNotification('Error al actualizar variable', 'error');
            }
        }
    };

    // ===== FUNCIONES DE ALARMA =====
    const handleSaveAlarm = async () => {
        const min = parseFloat(minValue);
        const max = parseFloat(maxValue);

        if (isNaN(min) || isNaN(max)) {
            showNotification('Los valores deben ser números válidos', 'error');
            return;
        }

        if (min >= max) {
            showNotification('El valor mínimo debe ser menor que el máximo', 'error');
            return;
        }

        const alarmExists = currentVariable?.nb_alarm_min !== null;

        try {
            const fecha = new Date();

            if (alarmExists) {
                // Actualizar alarma existente
                const dataSend = {
                    nb_alarm_idn: parseInt(currentVariable.nb_alarm_idn),
                    nb_alarm_min: min,
                    nb_alarm_max: max,
                    vr_adt_usureg: user,
                    dt_adt_fchreg: fecha.toISOString()
                };
                await updateTableBd('updatealarm', dataSend);
                showNotification('Alarma actualizada correctamente', 'success');
            } else {
                // Crear nueva alarma
                const dataSend = {
                    nb_var_idn: parseInt(currentVariable.nb_var_idn),
                    nb_tldro_idn: parseInt(rig),
                    nb_usurio_idn: uid,
                    nb_alarm_min: min,
                    nb_alarm_max: max,
                    vr_adt_usureg: user,
                    dt_adt_fchreg: fecha.toISOString()
                };
                await insertTableBd('createalarm', dataSend);
                showNotification('Alarma creada correctamente', 'success');
            }

            await onUpdate();
            handleClose();
        } catch (error) {
            console.error('Error al guardar alarma:', error);
            showNotification('Error al guardar alarma', 'error');
        }
    };

    const handleDeleteAlarm = async () => {
        showConfirmDialog(
            '¿Está seguro de eliminar esta alarma?',
            async () => {
                try {
                    await deleteBd(currentVariable.nb_alarm_idn, 'deleteVarAlarm');
                    showNotification('Alarma eliminada correctamente', 'success');
                    await onUpdate();
                    handleClose();
                } catch (error) {
                    console.error('Error al eliminar alarma:', error);
                    showNotification('Error al eliminar alarma', 'error');
                }
            },
            () => {
                console.log('Eliminación de alarma cancelada');
            }
        );
    };

    if (!show) return null;

    const alarmExists = currentVariable?.nb_alarm_min !== null;

    return (
        <div className="mvco-overlay" onClick={handleClose}>
            <div className="mvco-modal modal-themed-dialog" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="mvco-header">
                    <div className="mvco-title-wrapper">
                        <h2 className="mvco-title">
                            {updateVarMatrizId === 0 ? '➕ Agregar Variable' : '⚙️ Configurar Variable'}
                        </h2>
                        <p className="mvco-subtitle">{currentVariable?.vr_var_nom || 'Nueva variable'}</p>
                    </div>
                    <button 
                        className="mvco-close-btn"
                        onClick={handleClose}
                        aria-label="Cerrar"
                    >
                        ✕
                    </button>
                </div>

                {/* Tabs */}
                <div className="mvco-tabs">
                    <button
                        className={`mvco-tab ${activeTab === 'variable' ? 'mvco-tab-active' : ''}`}
                        onClick={() => setActiveTab('variable')}
                    >
                        📊 Variable
                    </button>
                    <button
                        className={`mvco-tab ${activeTab === 'alarm' ? 'mvco-tab-active' : ''}`}
                        onClick={() => setActiveTab('alarm')}
                        disabled={updateVarMatrizId === 0}
                    >
                        🚨 Alarma
                    </button>
                </div>

                {/* Body - Contenido según tab activo */}
                <div className="mvco-body">
                    {activeTab === 'variable' ? (
                        /* TAB DE VARIABLE */
                        <>
                            <div className="mvco-search-container">
                                <div className="mvco-search-wrapper">
                                    <span className="mvco-search-icon">🔍</span>
                                    <input
                                        type="text"
                                        className="mvco-search-input"
                                        placeholder="Buscar variable..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        autoFocus
                                    />
                                    {searchTerm && (
                                        <button 
                                            className="mvco-clear-search"
                                            onClick={() => setSearchTerm('')}
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>
                            </div>

                            {!variables || variables.length === 0 ? (
                                <div className="mvco-no-results">
                                    <div className="mvco-no-results-icon">⏳</div>
                                    <p className="mvco-no-results-text">
                                        Cargando variables...
                                    </p>
                                    <p className="mvco-no-results-hint">
                                        Si el problema persiste, recargue la página
                                    </p>
                                </div>
                            ) : filteredVariables.length === 0 ? (
                                <div className="mvco-no-results">
                                    <div className="mvco-no-results-icon">🔍</div>
                                    <p className="mvco-no-results-text">
                                        {searchTerm ? 'No se encontraron variables' : 'No hay variables disponibles'}
                                    </p>
                                </div>
                            ) : (
                                <div className="mvco-table-container">
                                    <table className="mvco-table">
                                        <thead className="mvco-thead">
                                            <tr>
                                                <th className="mvco-th">Variable</th>
                                                <th className="mvco-th mvco-th-unit">Unidad</th>
                                            </tr>
                                        </thead>
                                        <tbody className="mvco-tbody">
                                            {filteredVariables.map(variable => (
                                                <tr
                                                    key={variable.nb_var_idn}
                                                    className="mvco-tr"
                                                    onClick={() => handleVariableSelect(variable)}
                                                >
                                                    <td className="mvco-td mvco-td-name">
                                                        {variable.vr_var_nom}
                                                    </td>
                                                    <td className="mvco-td mvco-td-unit">
                                                        {variable.vr_var_umd || '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    ) : (
                        /* TAB DE ALARMA */
                        <div className="mvco-alarm-content">
                            <div className="mvco-form-group">
                                <label className="mvco-label">Valor Mínimo</label>
                                <input
                                    type="number"
                                    className="mvco-input"
                                    value={minValue}
                                    onChange={(e) => setMinValue(e.target.value)}
                                    placeholder="Ingrese valor mínimo"
                                    step="0.01"
                                />
                            </div>

                            <div className="mvco-form-group">
                                <label className="mvco-label">Valor Máximo</label>
                                <input
                                    type="number"
                                    className="mvco-input"
                                    value={maxValue}
                                    onChange={(e) => setMaxValue(e.target.value)}
                                    placeholder="Ingrese valor máximo"
                                    step="0.01"
                                />
                            </div>

                            <div className="mvco-info-box">
                                <span className="mvco-info-icon">ℹ️</span>
                                <p className="mvco-info-text">
                                    La alarma se activará cuando el valor esté fuera del rango [{minValue}, {maxValue}]
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="mvco-footer">
                    {activeTab === 'alarm' && alarmExists && (
                        <button 
                            className="mvco-delete-btn"
                            onClick={handleDeleteAlarm}
                        >
                            🗑️ Eliminar
                        </button>
                    )}
                    <div className="mvco-footer-right">
                        <button 
                            className="mvco-cancel-btn"
                            onClick={handleClose}
                        >
                            Cancelar
                        </button>
                        {activeTab === 'alarm' && (
                            <button 
                                className="mvco-save-btn"
                                onClick={handleSaveAlarm}
                            >
                                {alarmExists ? '✅ Actualizar' : '💾 Crear'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModalVariableConfigOpt;
